import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Link,
  Button,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';

const colors = {
  black: '#000000',
  darkRed: '#451a15',
  red: '#970000',
  yellow: '#fdb200',
  offWhite: '#f6ede7',
};

export default function CookiePolicy() {
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Cookie Policy - Ulpian</title>
        <meta name="description" content="Ulpian's Cookie Policy - Information about how we use cookies" />
      </Helmet>

      <Box sx={{
        minHeight: '100vh',
        backgroundColor: colors.offWhite,
        pt: 10,
        pb: 6
      }}>
        <Container maxWidth="md">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{
              mb: 3,
              color: colors.darkRed,
              '&:hover': {
                backgroundColor: colors.yellow + '20',
              }
            }}
          >
            Back to Home
          </Button>

          <Paper sx={{ p: { xs: 3, md: 6 }, backgroundColor: 'white' }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                mb: 4,
                color: colors.darkRed,
                fontFamily: 'TanAshford, serif',
                fontWeight: 'bold'
              }}
            >
              Ulpian's Cookie Policy
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                Introduction
              </Typography>
              <Typography paragraph>
                In the spirit of transparency, this Cookie Policy ("Policy") provides detailed information
                about cookies and related technologies. It applies to Ulpian - web based application as well
                as Ulpian websites (collectively referred to as "Ulpian website"). The websites are operated
                by ULPIAN AI.
              </Typography>
              <Typography paragraph>
                In addition to this Policy, further general information on how we use, store, and keep your
                personal data secure is provided in our Privacy Notice.
              </Typography>
              <Typography paragraph>
                From time to time, we may update this Policy due to changes in applicable legal framework,
                technology or our business model. If we do, we will notify you by posting the Policy on our
                website with a new effective date, prior to the update becoming effective.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                General Information About Cookies and Related Technologies
              </Typography>
              <Typography paragraph>
                Cookies are small text files that we and associated third parties place on your device
                (e.g. computer or smartphone) when you visit our website and web-based interface. These
                files generally contain a string of alphanumeric characters, and they allow our servers
                to recognize your session in order to properly load the website for you and provide you
                with the requested service (e.g. to log in). Some cookies are necessary for the website
                to properly function, so they cannot be turned off. Other cookies are optional because
                they are not strictly necessary for the website to function, even though they might
                provide additional functionalities. Optional cookies can be turned off.
              </Typography>
              <Typography paragraph>
                Aside from the necessary versus optional distinction, cookies are also sometimes categorized
                based on the domain which sets them (first vs third-party ones) and on the duration for
                which they are stored on your device (session vs persistent).
              </Typography>
              <Typography paragraph>
                In addition to cookies, we might also use related technologies – such as web beacons
                (including tracking pixels) and HTTP requests in general – on our website. These
                technologies allow for standard information related to website browsing such as IP
                address, browser type, and the pages you have visited to be transferred to us or to
                associated third parties.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                Cookies Used by ULPIAN AI
              </Typography>

              <Typography variant="h6" sx={{ mt: 3, mb: 1, color: colors.red }}>
                Strictly Necessary Cookies
              </Typography>
              <Typography paragraph>
                We use Strictly Necessary cookies to ensure our website is functioning properly. For
                instance, to help you complete common online requests, like setting your privacy
                preferences, logging onto your account, and completing forms online. These cookies
                are set by default and cannot be disabled.
              </Typography>

              <Typography variant="h6" sx={{ mt: 3, mb: 1, color: colors.red }}>
                Functional Cookies
              </Typography>
              <Typography paragraph>
                We use Functional cookies to improve the functionality and personalization on our
                website and the services that we offer. If you reject these cookies some or all of
                the services may not function properly.
              </Typography>

              <Typography variant="h6" sx={{ mt: 3, mb: 1, color: colors.red }}>
                Performance Cookies
              </Typography>
              <Typography paragraph>
                Performance cookies allow us to measure website traffic and the popularity of pages.
                This data collected is used to measure and improve the website experience for our
                clients and visitors to the website. If you turn off these cookies your personal
                information will not be included in our aggregated data, and we will not be able
                to know when you have visited our site.
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                Managing Cookies
              </Typography>
              <Typography paragraph>
                You can change your cookie preferences at any time using our cookie preference tool.
                By choosing "Accept all cookies," you agree to the use of all cookies on our website.
                You can also reject all cookies (except the Strictly Necessary Cookies) by clicking
                "Reject All" and you can control the cookie types you wish to allow by selecting
                "Manage cookies" below. Rejecting cookies won't prevent you from accessing Ulpian's website.
              </Typography>
              <Typography paragraph>
                If you are concerned about having cookies on your computer, you can set your browser
                to refuse all cookies or to indicate when a cookie is being set, allowing you to
                decide whether to accept it. You can also delete cookies from your device. The help
                feature on most web browsers will tell you how to prevent your browser from accepting
                new cookies, how to receive notice when a new cookie is set, and how to disable
                cookies altogether. However, if you choose to block or delete cookies, certain
                features of our Sites may not operate correctly.
              </Typography>
              <Typography paragraph>
                For further information about deleting or blocking cookies, please visit:{' '}
                <Link
                  href="http://www.allaboutcookies.org/manage-cookies/"
                  target="_blank"
                  rel="noopener"
                  sx={{ color: colors.red }}
                >
                  http://www.allaboutcookies.org/manage-cookies/
                </Link>
              </Typography>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                Data Security and Privacy
              </Typography>
              <Typography paragraph>
                We take the security of your data seriously:
              </Typography>
              <ul>
                <li>All cookies are transmitted over encrypted connections (TLS 1.3)</li>
                <li>We never use cookies to track you across other websites</li>
                <li>Cookie data is never used to train or improve our AI models</li>
                <li>All data is stored exclusively within the European Union</li>
              </ul>
            </Box>

            <Box>
              <Typography variant="h5" sx={{ mb: 2, color: colors.darkRed }}>
                Contact Details
              </Typography>
              <Typography paragraph>
                If you have any questions regarding this Policy, please feel free to reach out to us at:
              </Typography>
              <Typography paragraph>
                <strong>ULPIAN AI d.o.o.</strong><br />
                Zelinska 5<br />
                10000 Zagreb<br />
                Croatia<br /><br />
                Email:{' '}
                <Link
                  href="mailto:info@ulpian.com"
                  sx={{ color: colors.red }}
                >
                  info@ulpian.com
                </Link>
              </Typography>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
